import { ChevronDownIcon } from '@radix-ui/react-icons';

import { Button } from '../../../../primitives/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '../../../../primitives/drawer';
import { InputNumber } from '../../../../primitives/input-number';
import { ScrollArea, ScrollBar } from '../../../../primitives/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../primitives/tabs';
import { cn } from '../../../../utils/cn';
import { CalendarDatePickerV2, MEXICO_TIMEZONE } from '../../../calendar/calendar-date-picker-v2';
import { LabelWithoutRef } from '../../../label/label-without-ref';
import { SelectModel } from '../../../select/select-model';
import type { TFact } from '../../types';

import { getDateValues, getDateValueString } from './get-date-value';

const DATE_TYPE = {
  regular: 'regular',
  dynamic: 'dynamic',
} as const;

const DYNAMIC_MODEL = [
  { section: 'Presets' },
  { value: 'today', item: 'today' },
  { section: 'Datos del usuario' },
];

interface InputDateProps {
  dateFactsModel?: TFact[];
  disabled?: boolean;
  isInvalid?: boolean;
  value: string | number;
  onChange?: (props: { value: string | number }) => void;
}

export function InputDate({
  dateFactsModel = [],
  disabled,
  isInvalid = false,
  value,
  onChange,
}: InputDateProps) {
  const { baseDate, years, months, days, hours, minutes, seconds } = getDateValues({ value });
  const currentTab = typeof baseDate === 'number' ? DATE_TYPE.regular : DATE_TYPE.dynamic;

  const buildDynamicModel = (dateFactsModel: TFact[]) => {
    return [
      ...DYNAMIC_MODEL,
      ...dateFactsModel.map(({ attributeName }) => ({ value: attributeName, item: attributeName })),
    ];
  };

  const buildDateMath = (overrides: Record<string, unknown> = {}) => {
    const payload: Record<string, string | number | undefined> = {
      baseDate,
      years,
      months,
      days,
      hours,
      minutes,
      seconds,
      ...overrides,
    };

    const args = Object.entries(payload)
      .filter(([_, v]) => v !== undefined && v !== '' && v !== null)
      .map(([k, v]) => (k === 'baseDate' ? `${k}: "${v}"` : `${k}: ${v}`))
      .join(', ');

    return `dateMath({ ${args} })`;
  };

  return (
    <Drawer handleOnly>
      <DrawerTrigger asChild>
        <Button
          className={cn('w-full justify-between gap-1 px-3 text-sm', {
            'border-destructive': isInvalid,
          })}
          disabled={disabled}
          variant="outline"
        >
          <span>
            {getDateValueString({ value }).length > 0
              ? getDateValueString({ value })
              : 'Seleccionar fecha'}
          </span>
          <ChevronDownIcon className="size-4 opacity-50" />
        </Button>
      </DrawerTrigger>
      <DrawerContent>
        <DrawerHeader className="p-0">
          <DrawerTitle className="sr-only">Seleccionar fecha</DrawerTitle>
          <DrawerDescription className="sr-only">Seleccionar fecha</DrawerDescription>
        </DrawerHeader>

        <ScrollArea className="grid size-full">
          <div className="m-auto w-full pb-4 max-md:px-10 md:w-[650px]">
            <Tabs defaultValue={currentTab}>
              <TabsList className="rounded-[1.1rem] p-1">
                <TabsTrigger className="px-3 py-1" value={DATE_TYPE.regular}>
                  Fecha regular
                </TabsTrigger>
                <TabsTrigger className="px-3 py-1" value={DATE_TYPE.dynamic}>
                  Fecha dinámica
                </TabsTrigger>
              </TabsList>

              <TabsContent value={DATE_TYPE.regular}>
                <div className="flex min-h-[24rem] items-center justify-center rounded-3xl border border-input shadow-xs">
                  <CalendarDatePickerV2
                    type="plain"
                    date={typeof baseDate === 'number' ? new Date(baseDate) : undefined}
                    disabled={disabled}
                    mode="single"
                    timeZone={MEXICO_TIMEZONE}
                    yearsRange={200}
                    onChange={(date) => {
                      const selectedDate = date?.setHours(0, 0, 0, 0);
                      selectedDate && onChange?.({ value: selectedDate });
                    }}
                  />
                </div>
              </TabsContent>

              <TabsContent value={DATE_TYPE.dynamic}>
                <div className="flex min-h-[24rem] flex-col gap-4">
                  <SelectModel
                    disabled={disabled}
                    model={buildDynamicModel(dateFactsModel)}
                    value={typeof baseDate === 'string' ? baseDate : ''}
                    onValueChange={(value) => {
                      onChange?.({ value: buildDateMath({ baseDate: value }) });
                    }}
                  />

                  <p className="px-3 text-sm leading-relaxed text-muted-foreground">
                    Si lo necesitas, especifica los años, meses o días que quieres{' '}
                    <span className="font-bold text-primary">sumar o restar</span> a la fecha
                    dinámica seleccionada.
                  </p>

                  <div className="grid grid-cols-[1fr_2fr] items-center gap-2 pl-3">
                    <LabelWithoutRef>Años</LabelWithoutRef>
                    <InputNumber
                      disabled={disabled}
                      max={1000}
                      min={-1000}
                      placeholder="0"
                      value={years ?? 0}
                      onValueChange={(value) => {
                        onChange?.({
                          value: buildDateMath({
                            baseDate: typeof baseDate === 'number' ? '' : baseDate,
                            years: value === 0 ? undefined : value,
                          }),
                        });
                      }}
                    />
                  </div>

                  <div className="grid grid-cols-[1fr_2fr] items-center gap-2 pl-3">
                    <LabelWithoutRef>Meses</LabelWithoutRef>
                    <InputNumber
                      disabled={disabled}
                      max={240}
                      min={-240}
                      placeholder="0"
                      value={months ?? 0}
                      onValueChange={(value) => {
                        onChange?.({
                          value: buildDateMath({
                            baseDate: typeof baseDate === 'number' ? '' : baseDate,
                            months: value === 0 ? undefined : value,
                          }),
                        });
                      }}
                    />
                  </div>

                  <div className="grid grid-cols-[1fr_2fr] items-center gap-2 pl-3">
                    <LabelWithoutRef>Días</LabelWithoutRef>
                    <InputNumber
                      disabled={disabled}
                      max={365}
                      min={-365}
                      placeholder="0"
                      value={days ?? 0}
                      onValueChange={(value) => {
                        onChange?.({
                          value: buildDateMath({
                            baseDate: typeof baseDate === 'number' ? '' : baseDate,
                            days: value === 0 ? undefined : value,
                          }),
                        });
                      }}
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
          <ScrollBar orientation="vertical" />
        </ScrollArea>

        <DrawerFooter className="border-t px-0">
          <div className="m-auto w-full max-md:px-10 md:w-[650px]">
            <DrawerClose asChild>
              <Button className="w-full">Continuar</Button>
            </DrawerClose>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
